import * as z from "zod";

function convertPersianArabicDigits(value: string): string {
  return value
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString())
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString());
}

function normalizeNationalId(value: string): string {
  return convertPersianArabicDigits(value.trim());
}

function normalizePassport(value: string): string {
  return convertPersianArabicDigits(value.trim()).toUpperCase();
}

function normalizePhone(value: string): string {
  return convertPersianArabicDigits(value.trim());
}

function isValidNationalId(value: string): boolean {
  const normalized = normalizeNationalId(value);
  if (!/^\d{10}$/.test(normalized)) return false;
  if (/^(\d)\1{9}$/.test(normalized)) return false;

  const check = Number(normalized[9]);
  const sum = Array.from(normalized.slice(0, 9)).reduce(
    (acc, digit, index) => acc + Number(digit) * (10 - index),
    0,
  );
  const remainder = sum % 11;
  return remainder < 2 ? check === remainder : check === 11 - remainder;
}

function isValidPassport(value: string): boolean {
  const normalized = normalizePassport(value);
  return /^[A-Za-z0-9]{6,9}$/.test(normalized);
}

export const signupSchema = z
  .object({
    fName: z.string().min(2, "لطفاً نام خود را وارد کنید"),
    lName: z.string().min(2, "لطفاً نام خانوادگی خود را وارد کنید"),
    phNum: z
      .string()
      .nonempty("لطفاً شماره تماس خود را وارد کنید")
      .transform((val) => normalizePhone(val))
      .refine((val) => /^\+?[0-9]{7,15}$/.test(val), {
        message: "لطفاً شماره تماس معتبر وارد کنید",
      }),
    grade: z.string().nonempty("لطفاً مقطع تحصیلی را انتخاب کنید"),
    nation: z.enum(["iran", "nonIran"], {
      required_error: "لطفاً تابعیت خود را مشخص کنید",
    }),
    natCode: z.string().nonempty("لطفاً کدملی یا کد پاسپورت را وارد کنید"),
    province: z.string().optional(),
    county: z.string().optional(),
    address: z
      .string()
      .optional()
      .refine(
        (value) => !value || value.length >= 10,
        {
          message: "لطفا آدرس صحیح وارد کنید",
        }
      ),
  })
  .superRefine((data, ctx) => {
    if (data.nation === "iran") {
      if (!isValidNationalId(data.natCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["natCode"],
          message: "کدملی وارد شده معتبر نیست",
        });
      }
       if (!data.province) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["province"],
          message: "لطفاً استان را انتخاب کنید",
        });
      }

      if (!data.county) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["county"],
          message: "لطفاً شهرستان را انتخاب کنید",
        });
      } 
    } else {
      if (!isValidPassport(data.natCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["natCode"],
          message: "کد پاسپورت وارد شده معتبر نیست",
        });
      }
    }
  });

export type SignupFormValues = z.infer<typeof signupSchema>;